/**
 * This file is used to provide the translation loader for the application.
 * It has been adapted from this GitHub Issue: Support for Angular v18
 * - https://github.com/ngx-translate/core/issues/1481
 */

import { HttpBackend } from '@angular/common/http';
import {
  EnvironmentProviders,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModuleConfig,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

function HttpLoaderFactory(http: HttpBackend) {
  return new MultiTranslateHttpLoader(http, [
    {
      prefix: './assets/i18n/',
      suffix: `.json?v=${environment.buildNumber}`,
    },
    {
      prefix: './assets/i18n/overrides.',
      suffix: `.json?v=${environment.buildNumber}`,
      optional: true,
    },
  ]);
}
export const provideTranslation = () =>
  ({
    lang: getLanguageFromLocalStorage(),
    fallbackLang: environment.defaultLanguage,
    loader: {
      provide: TranslateLoader,
      useFactory: HttpLoaderFactory,
      deps: [HttpBackend],
    },
    compiler: {
      provide: TranslateCompiler,
      useClass: TranslateMessageFormatCompiler,
    },
  }) as TranslateModuleConfig;

export const provideTranslationInitializer = (): EnvironmentProviders => {
  return provideAppInitializer(async () => {
    const translateService = inject(TranslateService);
    await firstValueFrom(translateService.use(getLanguageFromLocalStorage()));
  });
};

const LANGUAGE_LOCAL_STORAGE_KEY = 'squadbox.language';
export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const getLanguageFromLocalStorage = () => {
  const language = localStorage.getItem(LANGUAGE_LOCAL_STORAGE_KEY);
  if (language) {
    return language;
  }
  return environment.defaultLanguage;
};

export const setLanguageInLocalStorageAndReload = (
  language: SupportedLanguage,
) => {
  localStorage.setItem(LANGUAGE_LOCAL_STORAGE_KEY, language);
  window.location.reload();
};

/**
 * Narrows an arbitrary string (e.g. the active TranslateService language)
 * to one of the supported languages, falling back to `fallback` when it
 * doesn't match. Centralizes the narrowing logic so callers don't need
 * inline `as` casts to use `'en' | 'fr'` typed values.
 */
export const resolveSupportedLanguage = (
  language: string,
  fallback: SupportedLanguage,
): SupportedLanguage => {
  for (const supported of SUPPORTED_LANGUAGES) {
    if (supported === language) {
      return supported;
    }
  }
  return fallback;
};
